/* eslint-disable @typescript-eslint/no-explicit-any */
let youtubeApiPromise: Promise<any> | null = null

export type YouTubePlayer = {
	playVideo: () => void
	pauseVideo: () => void
	cueVideoById: (videoId: string, startSeconds?: number) => void
	seekTo: (seconds: number, allowSeekAhead?: boolean) => void
	getCurrentTime: () => number
	getDuration: () => number
	getPlayerState: () => number
	getPlaybackRate: () => number
	setPlaybackRate: (rate: number) => void
	mute: () => void
	isMuted: () => boolean
	/** `null` once the player is destroyed. */
	getIframe: () => HTMLIFrameElement | null
	addEventListener: (
		event: 'onStateChange',
		listener: (event: YouTubePlayerEvent) => void
	) => void
	removeEventListener: (
		event: 'onStateChange',
		listener: (event: YouTubePlayerEvent) => void
	) => void
	destroy: () => void
}

export type YouTubePlayerEvent = {
	data: number
}

/**
 * What a player is doing. `cued` is a film loaded but never played: the
 * player shows its picture, not a frame of it.
 */
export type YouTubePhase =
	| 'unstarted'
	| 'ended'
	| 'playing'
	| 'paused'
	| 'buffering'
	| 'cued'

/** The IFrame API's `YT.PlayerState` numbers, which it documents as stable. */
const PHASES: Record<number, YouTubePhase> = {
	[-1]: 'unstarted',
	0: 'ended',
	1: 'playing',
	2: 'paused',
	3: 'buffering',
	5: 'cued',
}

export const youTubePhase = (playerState: number): YouTubePhase =>
	PHASES[playerState] ?? 'unstarted'

export type YouTubeIframeApi = {
	Player: new (
		container: HTMLDivElement,
		config: {
			videoId: string
			playerVars: Record<string, string | number>
			events: {
				onReady?: () => void
				onStateChange?: (event: YouTubePlayerEvent) => void
			}
		}
	) => YouTubePlayer
	PlayerState: {
		ENDED: number
		PLAYING: number
		PAUSED: number
		BUFFERING: number
	}
}

export const loadYouTubeIframeApi = (): Promise<any> => {
	if (typeof window === 'undefined') {
		return Promise.reject(new Error('Window is not available'))
	}

	if (window.YT && window.YT.Player) {
		return Promise.resolve(window.YT)
	}

	if (!youtubeApiPromise) {
		youtubeApiPromise = new Promise((resolve, reject) => {
			const existingScript = document.getElementById('youtube-iframe-api')
			if (!existingScript) {
				const script = document.createElement('script')
				script.id = 'youtube-iframe-api'
				script.src = 'https://www.youtube.com/iframe_api'
				script.async = true
				script.onerror = () => reject(new Error('Failed to load YouTube API'))
				document.body.appendChild(script)
			}

			const previousReady = window.onYouTubeIframeAPIReady
			window.onYouTubeIframeAPIReady = () => {
				if (typeof previousReady === 'function') {
					previousReady()
				}
				resolve(window.YT)
			}
		})
	}

	return youtubeApiPromise
}

export const extractYouTubeVideoId = (link: string): string | null => {
	try {
		const url = new URL(link)
		const host = url.hostname.toLowerCase()

		if (host === 'youtu.be' || host.endsWith('.youtu.be')) {
			const id = url.pathname.replace('/', '').trim()
			return id || null
		}

		if (host.includes('youtube.com')) {
			if (url.pathname === '/watch') {
				return url.searchParams.get('v')
			}

			if (url.pathname.startsWith('/embed/')) {
				return url.pathname.replace('/embed/', '').split('/')[0] || null
			}

			if (url.pathname.startsWith('/v/')) {
				return url.pathname.replace('/v/', '').split('/')[0] || null
			}

			if (url.pathname.startsWith('/shorts/')) {
				return url.pathname.replace('/shorts/', '').split('/')[0] || null
			}
		}
	} catch {
		return null
	}

	return null
}

declare global {
	interface Window {
		YT?: any
		onYouTubeIframeAPIReady?: () => void
	}
}
